import { useCanGoBack, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, BoxIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isElectron } from "../../env";
import { projectVmRoute } from "../../projectVmNavigation";
import type {
  SidebarProjectGroupMember,
  SidebarProjectSnapshot,
} from "../../sidebarProjectGrouping";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { SidebarInset } from "../ui/sidebar";
import {
  WorkspaceBreadcrumb,
  WorkspaceBreadcrumbItem,
  WorkspaceBreadcrumbSeparator,
} from "../WorkspaceBreadcrumb";
import { WorkspacePageContainer } from "../WorkspacePageContainer";
import { WorkspacePageHeader } from "../WorkspacePageHeader";
import { ProjectVmSettings } from "./ProjectVmSettings";
import { useSettingsProjectGroups } from "./useSettingsProjectGroups";

function representativeCheckout(group: SidebarProjectSnapshot): SidebarProjectGroupMember {
  return (
    group.memberProjects.find(
      (member) => member.environmentId === group.environmentId && member.id === group.id,
    ) ?? group.memberProjects[0]!
  );
}

function checkoutLabel(project: SidebarProjectGroupMember): string {
  const environment = project.environmentLabel ?? "This machine";
  return `${environment} · ${project.workspaceRoot}`;
}

function VmEnvironmentContent({ group }: { group: SidebarProjectSnapshot }) {
  const representative = useMemo(() => representativeCheckout(group), [group]);
  const [selectedCheckoutKey, setSelectedCheckoutKey] = useState(representative.physicalProjectKey);
  const selectedCheckout =
    group.memberProjects.find((member) => member.physicalProjectKey === selectedCheckoutKey) ??
    representative;

  return (
    <div className="topbar-scroll-fade scrollbar-gutter-both flex-1 overflow-y-auto [--topbar-scroll-fade-height:1.5rem]">
      <WorkspacePageContainer width="readable" className="gap-10">
        <div className="flex flex-col gap-4 px-3 sm:flex-row sm:items-start sm:justify-between sm:px-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <BoxIcon aria-hidden className="size-4.5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold tracking-[-0.03em]">VM environment</h1>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
                Define how {group.displayName} is installed, started, and saved as a reusable Build
                for new VM tasks.
              </p>
            </div>
          </div>

          {group.memberProjects.length > 1 ? (
            <Select
              value={selectedCheckout.physicalProjectKey}
              onValueChange={(value) => setSelectedCheckoutKey(String(value))}
            >
              <SelectTrigger className="w-full max-w-full sm:w-72" aria-label="VM checkout">
                <SelectValue>{checkoutLabel(selectedCheckout)}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {group.memberProjects.map((member) => (
                  <SelectItem
                    key={member.physicalProjectKey}
                    hideIndicator
                    value={member.physicalProjectKey}
                  >
                    {checkoutLabel(member)}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          ) : null}
        </div>

        <ProjectVmSettings key={selectedCheckout.physicalProjectKey} project={selectedCheckout} />
      </WorkspacePageContainer>
    </div>
  );
}

export function ProjectVmEnvironmentPage({ projectKey }: { projectKey: string }) {
  const groups = useSettingsProjectGroups();
  const selected = groups.find((group) => group.projectKey === projectKey) ?? null;
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const lastSelectionRef = useRef<{ key: string; memberKeys: string[] } | null>(null);

  useEffect(() => {
    if (!selected) return;
    lastSelectionRef.current = {
      key: selected.projectKey,
      memberKeys: selected.memberProjects.map((member) => member.physicalProjectKey),
    };
  }, [selected]);

  useEffect(() => {
    if (selected !== null) return;
    const last = lastSelectionRef.current;
    if (last?.key !== projectKey) return;
    const successor = groups.find((group) =>
      group.memberProjects.some((member) => last.memberKeys.includes(member.physicalProjectKey)),
    );
    if (!successor) return;
    void navigate({
      ...projectVmRoute(successor.projectKey),
      replace: true,
      hashScrollIntoView: false,
    });
  }, [groups, navigate, projectKey, selected]);

  const navigateBackWithinApp = useCallback(() => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: "/projects/$projectKey", params: { projectKey } });
  }, [canGoBack, navigate, projectKey]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== "Escape") return;
      event.preventDefault();
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) activeElement.blur();
      navigateBackWithinApp();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigateBackWithinApp]);

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
        <WorkspacePageHeader electron={isElectron}>
          <Button
            type="button"
            size="icon-xs"
            variant="ghost-muted"
            aria-label="Back from VM environment"
            onClick={navigateBackWithinApp}
          >
            <ArrowLeftIcon aria-hidden className="size-3.5" />
          </Button>
          <WorkspaceBreadcrumb ariaLabel="VM environment breadcrumb">
            <WorkspaceBreadcrumbItem>
              <button
                type="button"
                className="truncate rounded-sm text-left hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() =>
                  void navigate({ to: "/projects/$projectKey", params: { projectKey } })
                }
              >
                {selected?.displayName ?? "Project"}
              </button>
            </WorkspaceBreadcrumbItem>
            <WorkspaceBreadcrumbSeparator />
            <WorkspaceBreadcrumbItem current>VM environment</WorkspaceBreadcrumbItem>
          </WorkspaceBreadcrumb>
        </WorkspacePageHeader>

        {selected ? (
          <VmEnvironmentContent key={selected.projectKey} group={selected} />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
            {groups.length === 0
              ? "Add a project from the sidebar before configuring a VM environment."
              : "This project is no longer available."}
          </div>
        )}
      </div>
    </SidebarInset>
  );
}
